import { create } from 'zustand';
import {
  getAllCrmCategories,
  getCrmCategories,
  getCustomerData,
  getCxComplaintTicket,
  getTicketConfig,
  getTicketConfigByOrderId,
  getTicketSlot,
  postTicket,
  updateTicketsCaseIds
} from '../api';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { persist } from 'zustand/middleware';

export const useOrderSearchForTicket = create((set, get) => ({
  customerDetails: [],
  crmCategories: [],
  allCrmCategories: [],
  isLoading: false,
  isError: false,
  fetchCustomerDetails: async (key) => {
    try {
      const response = await getCustomerData(key);
      set({ customerDetails: response.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch customer details.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
  fetchCrmCategories: async () => {
    try {
      const response = await getCrmCategories();
      set({ crmCategories: response.data.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch CRM categories.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
  fetchAllCrmCategories: async () => {
    try {
      const response = await getAllCrmCategories();
      set({ allCrmCategories: response.data.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch all CRM categories.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
}));

export const useCreateTicket = create(set => ({
  ticketResponse: {},
  ticketLoading: false,
  createTicket: async (payload, key) => {
    try {
      set({ ticketLoading: true });
      const response = await postTicket(payload);
      set({ ticketResponse: response.data });
      toast.success('Ticket Created');
      return { success: true };
    } catch (error) {
      const statusCode = _.get(error, ['response', 'status']);
      const fallbackError = _.get(error, ['message'], 'Failed to Create Ticket.');
      const errorMessage = _.get(error, ['response', 'data', 'message'], fallbackError);
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);

      if (statusCode === 409) {
        toast.warn(errorMessage.replaceAll('_', ' '));
      } else if (statusCode !== 422) {
        toast.error(nestedErrorMessage);
      }
      return { success: false, status: statusCode, message: nestedErrorMessage };
    } finally {
      set({ ticketLoading: false });
    }
  },
}));

export const useTicketConfig = create((set, get) => ({
  ticketConfig: {},
  ticketConfigByOrderId: {},

  fetchTicketConfig: async caseId => {
    if (!caseId) return null;
    try {
      const response = await getTicketConfig(caseId);
      set({ ticketConfig: response.data });
      return response.data; // ✅ always return
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Something went wrong');
      toast.error(errorMessage);
      return null;
    }
  },

  fetchTicketConfigByOrderId: async (orderId, category) => {
    try {
      const response = await getTicketConfigByOrderId(orderId, category);
      const data = _.get(response, ['data', orderId ], {});
      set({ ticketConfigByOrderId: data });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Something went wrong');
      toast.error(errorMessage);
      return null;
    }
  },
}));

export const useTicketSlots = create(set => ({
	ticketSlots: {},
  ticketSlotLoading: false,
  fetchTicketSlotData: async (category, language) => {
    try {
      set({ allSlotLoading: true, error: null });
      const response = await getTicketSlot(category, language);
      set({ ticketSlots: response.data, ticketSlotLoading: false });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      set({ error: error.message, ticketSlotLoading: false });
    }
  }
}));

export const useCxComplaintTicket = create(set => ({
	ticketData: [],
  ticketLoading: false,
  error: false,
  rowCount: 0,
  fetchCxComplaintTicket: async (sd, ed, newPage, query) => {
    try {
      set({ ticketLoading: true, error: null });
      const response = await getCxComplaintTicket(sd, ed, newPage, query);
      const { row_count, tickets } = response.data;
      set({ ticketData: tickets, rowCount: row_count, ticketLoading: false });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      set({ error: error.message, ticketLoading: false });
    }
  }
}));

export const useMapMissingTicket = create(set => ({
  updatedTickets: null,
  updateTicketsLoading: false,
  updateTicketsError: null,

  updateTicketsCaseIds: async (ticketIds) => {
    try {
      set({ updateTicketsLoading: true, updateTicketsError: null });
      const response = await updateTicketsCaseIds(ticketIds);

      if (response.data.success) {
        set({
          updatedTickets: response.data.updatedTickets,
          updateTicketsLoading: false
        });
        toast.success(response.data.message);
      } else {
        set({
          updateTicketsError: response.data.message,
          updateTicketsLoading: false,
          updatedTickets: []
        });
        toast.error(response.data.message);
      }
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to update tickets case IDs');
      set({
        updateTicketsError: errorMessage,
        updateTicketsLoading: false,
        updatedTickets: []
      });
      toast.error(errorMessage);
    }
  },

  clearResults: () => {
    set({
      updatedTickets: null,
      updateTicketsError: null
    });
  }
}));